import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import zktecoService from '../services/zktecoService';
import './ZKTecoAttendanceReview.css';

const ZKTecoAttendanceReview = () => {
  const { hasRole } = useAuth();
  const [pendingAttendance, setPendingAttendance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);
  const [filters, setFilters] = useState({
    status: 'PENDING',
    date: null,
  });
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [selectedAttendanceId, setSelectedAttendanceId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  useEffect(() => {
    loadPendingAttendance();
    // Rafraîchir toutes les 30 secondes
    const interval = setInterval(loadPendingAttendance, 30000);
    return () => clearInterval(interval);
  }, [filters.status]);

  const loadPendingAttendance = async () => {
    try {
      setLoading(true);
      const data = await zktecoService.getAttendanceByStatus(filters.status);
      setPendingAttendance(data);
      setError(null);
    } catch (err) {
      console.error('Erreur lors du chargement des pointages', err);
      setError('Erreur lors du chargement des pointages');
    } finally {
      setLoading(false);
    }
  };

  const handleValidate = async (id) => {
    try {
      await zktecoService.validateAttendance(id);
      loadPendingAttendance();
      alert('Pointage validé avec succès');
    } catch (err) {
      console.error('Erreur lors de la validation', err);
      alert('Erreur lors de la validation: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleReject = async (id, reason) => {
    try {
      await zktecoService.rejectAttendance(id, reason || 'Aucune raison spécifiée');
      setShowRejectDialog(false);
      setRejectReason('');
      setSelectedAttendanceId(null);
      loadPendingAttendance();
      alert('Pointage rejeté avec succès');
    } catch (err) {
      console.error('Erreur lors du rejet', err);
      alert('Erreur lors du rejet: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleBatchValidate = async () => {
    if (selectedItems.length === 0) {
      alert('Sélectionnez au moins un pointage à valider');
      return;
    }

    if (!window.confirm(`Valider ${selectedItems.length} pointage(s) ?`)) {
      return;
    }

    try {
      await zktecoService.batchValidateAttendance(selectedItems);
      setSelectedItems([]);
      loadPendingAttendance();
      alert(`${selectedItems.length} pointage(s) validé(s) avec succès`);
    } catch (err) {
      console.error('Erreur lors de la validation en lot', err);
      alert('Erreur lors de la validation en lot: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleSelectItem = (id) => {
    setSelectedItems((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectedItems.length === pendingAttendance.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(pendingAttendance.map((item) => item.id));
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'PENDING':
        return 'En attente';
      case 'SYNCED':
        return 'Synchronisé';
      case 'ERROR':
        return 'Erreur';
      case 'REJECTED':
        return 'Rejeté';
      default:
        return status;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'PENDING':
        return 'status-pending';
      case 'SYNCED':
        return 'status-synced';
      case 'ERROR':
        return 'status-error';
      case 'REJECTED':
        return 'status-rejected';
      default:
        return '';
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'CHECK_IN':
        return 'Entrée';
      case 'CHECK_OUT':
        return 'Sortie';
      default:
        return type || '-';
    }
  };

  if (loading && pendingAttendance.length === 0) {
    return (
      <div className="zkteco-attendance-review">
        <div className="loading">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="zkteco-attendance-review">
      <div className="review-header">
        <h1>Validation des Pointages ZKTeco</h1>
        <div className="header-actions">
          <select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            className="filter-select"
          >
            <option value="PENDING">En attente</option>
            <option value="SYNCED">Synchronisés</option>
            <option value="ERROR">Erreurs</option>
            <option value="REJECTED">Rejetés</option>
          </select>
          {selectedItems.length > 0 && filters.status === 'PENDING' && (
            <button className="btn btn-primary" onClick={handleBatchValidate}>
              Valider la sélection ({selectedItems.length})
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="alert alert-error">{error}</div>
      )}

      {showRejectDialog && (
        <div className="modal-overlay" onClick={() => setShowRejectDialog(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Rejeter le pointage</h2>
            <div className="form-group">
              <label>Raison du rejet</label>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Explication du rejet..."
                rows="4"
              />
            </div>
            <div className="form-actions">
              <button
                className="btn btn-danger"
                onClick={() => handleReject(selectedAttendanceId, rejectReason)}
              >
                Rejeter
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowRejectDialog(false);
                  setRejectReason('');
                  setSelectedAttendanceId(null);
                }}
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="attendance-list">
        {pendingAttendance.length === 0 ? (
          <div className="empty-state">
            <p>Aucun pointage {getStatusLabel(filters.status).toLowerCase()}</p>
          </div>
        ) : (
          <table className="attendance-table">
            <thead>
              <tr>
                {filters.status === 'PENDING' && (
                  <th>
                    <input
                      type="checkbox"
                      checked={selectedItems.length === pendingAttendance.length && pendingAttendance.length > 0}
                      onChange={handleSelectAll}
                    />
                  </th>
                )}
                <th>Date/Heure</th>
                <th>Employé</th>
                <th>Pointeuse</th>
                <th>Agent</th>
                <th>Type</th>
                <th>Statut</th>
                {filters.status === 'PENDING' && (
                  <th className="actions-column">Actions</th>
                )}
              </tr>
            </thead>
            <tbody>
              {pendingAttendance.map((attendance) => (
                <tr key={attendance.id}>
                  {filters.status === 'PENDING' && (
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedItems.includes(attendance.id)}
                        onChange={() => handleSelectItem(attendance.id)}
                      />
                    </td>
                  )}
                  <td>{formatDateTime(attendance.attendanceTime)}</td>
                  <td>
                    {attendance.employeeName || attendance.userId}
                  </td>
                  <td>{attendance.deviceName || attendance.deviceId || '-'}</td>
                  <td>{attendance.agentName || attendance.agentId || '-'}</td>
                  <td>{getTypeLabel(attendance.type)}</td>
                  <td>
                    <span className={`status-badge ${getStatusColor(attendance.syncStatus)}`}>
                      {getStatusLabel(attendance.syncStatus)}
                    </span>
                  </td>
                  {filters.status === 'PENDING' && (
                    <td className="actions-column">
                      <button
                        className="btn btn-success btn-small"
                        onClick={() => handleValidate(attendance.id)}
                        title="Valider"
                      >
                        Valider
                      </button>
                      <button
                        className="btn btn-danger btn-small"
                        onClick={() => {
                          setSelectedAttendanceId(attendance.id);
                          setShowRejectDialog(true);
                        }}
                        title="Rejeter"
                      >
                        Rejeter
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default ZKTecoAttendanceReview;

